import { DataTable, Dialog, FormSectionHeader, Loader, NoData } from "@/components"
import { Props } from "./types"
import useDocumentLineItemDialog from "./useDocumentLineItemDialog"
import { Button, Label, Switch } from "@/components/ui";
import { Download, ListFilter } from "lucide-react";
import { ColumnDrawer } from "./ColumnDrawer";
import { cn } from "@/lib/utils";

const DocumentLineItemDialog = ({
  open,
  setOpen,
  selectedDoc,
  dialog = true,
  showDownloadGLExcel,
  showNoDataOnError = false,
}: Props) => {
  const {isReportLoading, isReportError, reportData, columns, lineItems, onColumnClick, isHeader,
    isEntryView, setIsEntryView, openColumnDrawer, setOpenColumnDrawer,
    headerFields, lineItemFields, onSaveColumns, onDownloadExcel, isReportExportLoading
  } = useDocumentLineItemDialog({selectedDoc});

  const mainReport = () => {
    return (
      <>
        {isReportLoading ? <Loader /> : showNoDataOnError && (isReportError || !reportData) ? (
          <NoData />
        ) :
          <>
            <div className={cn("border border-g-200 rounded-[12px] p-5", dialog ? "" : "bg-white")}>
              <div className="flex items-center justify-between">
                <FormSectionHeader>
                  Document Header
                </FormSectionHeader>
                <div className="mb-6 flex items-start justify-end">
                  <Button
                    type="button"
                    className="h-[32px] bg-primary px-4 text-[12px] text-white"
                    onClick={() => onColumnClick("header")}
                  >
                    <ListFilter className="h-3.5 w-3.5" />
                    Column
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-y-5 gap-x-6">
                {
                  headerFields?.filter((data: any) => data.checked)?.map((data: any, index: number) => {
                    return (
                      <div key={`doc-header-${index}`}>
                        <div className="text-[11px] text-g-400">{data.label}</div>
                        <div className="text-[12px] font-[500]">
                          {data?.value || ''}
                        </div>
                      </div>
                    )
                  })
                }
              </div>
            </div>

            <div className={cn("mt-5 border border-g-200 rounded-[12px] pt-3 pb-3 overflow-hidden", dialog ? "" : "bg-white")}>
              <div className="flex flex-row items-center justify-between px-3">
                <div className="pb-3 text-primary text-[12px] font-[600]">
                  Document Lines
                </div>
                <div className="flex flex-row items-center gap-3">
                  <div className="w-full flex items-center justify-end gap-2 h-[38px] mb-5">
                    <Label className="text-[10px]">GL View</Label>
                    <Switch 
                      checked={isEntryView}
                      onCheckedChange={setIsEntryView}
                    />
                    <Label className="text-[10px]">Entry View</Label>
                  </div>
                  <div className="mb-6 flex items-start gap-5">
                    <Button
                      type="button"
                      className="h-[32px] bg-primary px-4 text-[12px] text-white"
                      onClick={() => onColumnClick("lineItem")}
                    >
                      <ListFilter className="h-3.5 w-3.5" />
                      Column
                    </Button>
                    {showDownloadGLExcel ? <Button
                      type="button"
                      className="h-[32px] bg-green-600 hover:bg-green-600 px-4 text-[12px] text-white"
                      onClick={onDownloadExcel}
                      disabled={isReportExportLoading}
                    >
                      Download GL Excel
                      {isReportExportLoading ? <Loader variant="secondary" /> : <Download className="h-3.5 w-3.5" /> }
                    </Button> : <></>}
                  </div>
                </div>
              </div>
              <DataTable
                data={lineItems || []}
                columns={columns || []}
                className="shadow-none border-none rounded-none"
                tableWrapperClassName="px-0"
                headerClass={{
                  row: "bg-blue-50 hover:bg-blue-50",
                  cell: "text-black"
                }}
                hideFooter
              />
            </div>
          </>
        }

        <ColumnDrawer
          open={openColumnDrawer}
          setOpen={setOpenColumnDrawer}
          columns={isHeader ? headerFields : lineItemFields}
          onSave={onSaveColumns} 
        />
      </>
    )
  }

  return dialog ? (
    <Dialog
      open={open as boolean}
      setOpen={setOpen}
      hideFooter
      contentClassName="sm:max-w-[1100px]"
      title={
        <div className="text-[16px] font-[600] text-g-400">
          Accounting Document - {" "}
          <span className="text-primary font-[600]">
            {selectedDoc?.postDocHeaderId}
          </span>
        </div>
      }
    >
      {mainReport()}
    </Dialog>
  ) : <>{mainReport()}</>
}

export default DocumentLineItemDialog;
